import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from '../config/axiosClient';
import SEO from '../components/common/SEO';

import Config from '../config/Config.js';
import { RESOLVE_LOCAL_AUTH_STATE, RESOLVE_LOCAL_USER_STATE } from '../components/auth/AuthActions';
import ProjectCard from '../components/project/ProjectCard';
import Welcome from '../components/marketing/Welcome';
import SweetLoadingSpinner from '../components/eyeCandy/SweetLoadingSpinner';

const PAGE_SIZE = 24;

const FEED_MODES = [
    { key: 'recent', label: 'Recent', ordering: '-created_at' },
    { key: 'trending', label: 'Trending', ordering: '-view_count' },
    { key: 'discover', label: 'Discover', ordering: '', shuffle: true },
];

const TECHNOLOGIES = [
    'Python', 'R', 'SQL', 'Mathematics', 'PySpark', 'Terraform',
    'Bash', 'Markdown', 'Django', 'LangChain',
];

const SKILLS = [
    'Data Engineering', 'Statistics', 'Programming', 'Machine Learning',
    'Time Series', 'Data Visualization', 'Web Development',
    'DevOps', 'Algorithms', 'AI Engineering',
];

export default function RootPage() {
    const auth = RESOLVE_LOCAL_AUTH_STATE();
    const user = RESOLVE_LOCAL_USER_STATE();
    const isAuth = auth.is_authenticated === 'true';

    const [mode, setMode] = useState('recent');
    const [techFilter, setTechFilter] = useState('');
    const [skillFilter, setSkillFilter] = useState('');
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nextUrl, setNextUrl] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [cols, setCols] = useState(() => {
        const saved = localStorage.getItem('i7-feed-cols');
        return saved ? parseInt(saved, 10) : 3;
    });
    const allFetchedRef = useRef([]);

    const currentMode = FEED_MODES.find((m) => m.key === mode) || FEED_MODES[0];

    const fetchProjects = useCallback(() => {
        setLoading(true);
        setProjects([]);
        setNextUrl(null);
        allFetchedRef.current = [];

        let url = `${Config().baseUrl}?publish=true&limit=${PAGE_SIZE}`;
        if (currentMode.ordering) url += `&ordering=${currentMode.ordering}`;
        if (techFilter || skillFilter) url += `&search=${encodeURIComponent(techFilter || skillFilter)}`;

        // For discover, fetch more to shuffle from
        const fetchUrl = currentMode.shuffle
            ? url.replace(`limit=${PAGE_SIZE}`, `limit=${PAGE_SIZE * 3}`)
            : url;

        axios.get(fetchUrl)
            .then((res) => {
                let results = res.data.results || [];
                setTotalCount(res.data.count || results.length);

                if (currentMode.shuffle) {
                    // Fisher-Yates shuffle
                    for (let i = results.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [results[i], results[j]] = [results[j], results[i]];
                    }
                    allFetchedRef.current = results;
                    setProjects(results.slice(0, PAGE_SIZE));
                    setNextUrl(results.length > PAGE_SIZE ? 'shuffle' : null);
                } else {
                    setProjects(results);
                    setNextUrl(res.data.next?.replace('http://', 'https://') || null);
                }
                setLoading(false);
            })
            .catch(() => {
                setProjects([]);
                setLoading(false);
            });
    }, [mode, techFilter, skillFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    const loadMore = useCallback(() => {
        if (!nextUrl || loadingMore) return;
        setLoadingMore(true);

        if (currentMode.shuffle) {
            const shown = projects.length;
            const next = allFetchedRef.current.slice(shown, shown + PAGE_SIZE);
            setProjects((prev) => [...prev, ...next]);
            setNextUrl(shown + PAGE_SIZE < allFetchedRef.current.length ? 'shuffle' : null);
            setLoadingMore(false);
        } else {
            axios.get(nextUrl)
                .then((res) => {
                    const results = res.data.results || [];
                    setProjects((prev) => [...prev, ...results]);
                    setNextUrl(res.data.next?.replace('http://', 'https://') || null);
                })
                .catch(() => {})
                .finally(() => setLoadingMore(false));
        }
    }, [nextUrl, loadingMore, projects.length, currentMode.shuffle]);

    const selectMode = (key) => {
        setMode(key);
        setTechFilter('');
        setSkillFilter('');
        setSidebarOpen(false);
    };

    const selectTech = (t) => {
        setTechFilter(techFilter === t ? '' : t);
        setSkillFilter('');
        setMode('recent');
        setSidebarOpen(false);
    };

    const selectSkill = (s) => {
        setSkillFilter(skillFilter === s ? '' : s);
        setTechFilter('');
        setMode('recent');
        setSidebarOpen(false);
    };

    const COL_CLASSES = {
        1: 'col-12',
        2: 'col-lg-6 col-md-6 col-12',
        3: 'col-xl-4 col-lg-4 col-md-6 col-12',
        4: 'col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12',
    };

    const setColCount = (n) => {
        setCols(n);
        localStorage.setItem('i7-feed-cols', String(n));
    };

    const activeFilter = techFilter || skillFilter || '';

    const seoJsonLd = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Inertia7",
        "url": "https://www.inertia7.com",
        "description": "A platform for creating and sharing software & data science projects.",
        "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.inertia7.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
        },
    };

    return (
        <div>
            <SEO
                title={activeFilter ? activeFilter : null}
                description={activeFilter
                    ? `Explore ${activeFilter} projects on Inertia7 — software & data science portfolio platform.`
                    : "Explore software & data science projects on Inertia7. Python, R, SQL, machine learning, data engineering, and more."
                }
                url="/"
                jsonLd={seoJsonLd}
            />
            {!isAuth && <Welcome />}

            <div style={{
                maxWidth: 1400, margin: '0 auto', padding: '24px 20px 60px',
                display: 'flex', gap: 32,
            }}>
                {/* Sidebar — desktop */}
                <nav style={{
                    width: 200, flexShrink: 0,
                    position: 'sticky', top: 80, alignSelf: 'flex-start',
                    display: 'none',
                }}
                className="feed-sidebar-desktop"
                >
                    <SidebarContent
                        mode={mode} selectMode={selectMode}
                        techFilter={techFilter} selectTech={selectTech}
                        skillFilter={skillFilter} selectSkill={selectSkill}
                        isAuth={isAuth} user={user}
                    />
                </nav>

                {/* Mobile sidebar toggle */}
                <button
                    className="feed-sidebar-toggle"
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    style={{
                        display: 'none', // shown via CSS on mobile
                        position: 'fixed', bottom: 20, left: 20, zIndex: 1000,
                        width: 48, height: 48, borderRadius: '50%',
                        border: 'none', background: 'var(--brand, #59c6c1)', color: '#fff',
                        fontSize: 20, cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}
                >
                    {sidebarOpen ? '\u2715' : '\u2630'}
                </button>

                {/* Mobile sidebar overlay */}
                {sidebarOpen && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        zIndex: 999, background: 'rgba(0,0,0,0.4)',
                    }} onClick={() => setSidebarOpen(false)}>
                        <nav
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                position: 'absolute', top: 0, left: 0, bottom: 0,
                                width: 260, background: 'var(--surface, #fff)',
                                padding: '80px 20px 20px', overflowY: 'auto',
                                boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
                            }}
                        >
                            <SidebarContent
                                mode={mode} selectMode={selectMode}
                                techFilter={techFilter} selectTech={selectTech}
                                skillFilter={skillFilter} selectSkill={selectSkill}
                                isAuth={isAuth} user={user}
                            />
                        </nav>
                    </div>
                )}

                {/* Main feed area */}
                <main style={{ flex: 1, minWidth: 0 }}>
                    {/* Feed header */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: 20, flexWrap: 'wrap', gap: 12,
                    }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {FEED_MODES.map((m) => (
                                <button
                                    key={m.key}
                                    onClick={() => selectMode(m.key)}
                                    style={{
                                        padding: '6px 16px', border: 'none', borderRadius: 20,
                                        fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 600,
                                        cursor: 'pointer',
                                        backgroundColor: mode === m.key && !activeFilter ? 'var(--brand, #59c6c1)' : 'var(--bg-soft, #f5f5f5)',
                                        color: mode === m.key && !activeFilter ? '#fff' : 'var(--ink, #333)',
                                        transition: 'all 150ms ease',
                                    }}
                                >
                                    {m.label}
                                </button>
                            ))}
                            {activeFilter && (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '6px 14px', borderRadius: 20,
                                    backgroundColor: 'var(--brand, #59c6c1)', color: '#fff',
                                    fontSize: '0.85rem', fontWeight: 600,
                                }}>
                                    {activeFilter}
                                    <span
                                        onClick={() => { setTechFilter(''); setSkillFilter(''); }}
                                        style={{ cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
                                    >&times;</span>
                                </span>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontFamily: 'Courier' }}>
                                {totalCount} project{totalCount !== 1 ? 's' : ''}
                            </span>
                            <div style={{ display: 'flex', gap: 2 }}>
                                {[1, 2, 3, 4].map((n) => (
                                    <button
                                        key={n}
                                        onClick={() => setColCount(n)}
                                        title={`${n} column${n > 1 ? 's' : ''}`}
                                        style={{
                                            width: 24, height: 24, padding: 0,
                                            border: 'none', borderRadius: 4,
                                            background: cols === n ? 'var(--brand, #59c6c1)' : 'transparent',
                                            color: cols === n ? '#fff' : 'var(--muted, #888)',
                                            cursor: 'pointer', fontSize: 11, fontWeight: 700,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'all 100ms ease',
                                        }}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Grid */}
                    {loading ? (
                        <SweetLoadingSpinner loadingState={true} />
                    ) : projects.length === 0 ? (
                        <p style={{ color: 'var(--muted)', padding: '40px 0', textAlign: 'center' }}>
                            No projects found{activeFilter ? ` for "${activeFilter}"` : ''}.
                        </p>
                    ) : (
                        <div className="row" style={{ margin: '0 -8px' }}>
                            {projects.map((project) => (
                                <ProjectCard
                                    project={project}
                                    key={project.id}
                                    colClass={COL_CLASSES[cols]}
                                />
                            ))}
                        </div>
                    )}

                    {/* Load more */}
                    {nextUrl && !loading && (
                        <div style={{ textAlign: 'center', padding: '24px 0' }}>
                            <button
                                onClick={loadMore}
                                disabled={loadingMore}
                                style={{
                                    padding: '10px 32px', border: '1px solid var(--border, #ddd)',
                                    borderRadius: 6, background: 'var(--surface, #fff)',
                                    color: 'var(--ink, #333)', fontSize: '0.9rem', fontWeight: 600,
                                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                                    opacity: loadingMore ? 0.6 : 1,
                                }}
                            >
                                {loadingMore ? 'Loading...' : 'Load more'}
                            </button>
                        </div>
                    )}
                </main>
            </div>

            {/* CSS for responsive sidebar */}
            <style>{`
                @media (min-width: 992px) {
                    .feed-sidebar-desktop { display: block !important; }
                    .feed-sidebar-toggle { display: none !important; }
                }
                @media (max-width: 991px) {
                    .feed-sidebar-toggle { display: flex !important; align-items: center; justify-content: center; }
                }
            `}</style>
        </div>
    );
}

/* ===== SIDEBAR ===== */

const sidebarLinkStyle = (active) => ({
    display: 'block', padding: '8px 12px', borderRadius: 6,
    fontSize: '0.85rem', fontWeight: active ? 700 : 500,
    color: active ? 'var(--brand, #59c6c1)' : 'var(--ink, #333)',
    backgroundColor: active ? 'var(--bg-soft, #f5f5f5)' : 'transparent',
    cursor: 'pointer', textDecoration: 'none',
    transition: 'all 100ms ease',
});

const sidebarHeadingStyle = {
    fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: 'var(--muted, #888)',
    padding: '16px 12px 4px', margin: 0,
};

const SidebarContent = ({ mode, selectMode, techFilter, selectTech, skillFilter, selectSkill, isAuth, user }) => (
    <>
        <div style={{ marginBottom: 8 }}>
            {FEED_MODES.map((m) => (
                <div key={m.key} style={sidebarLinkStyle(mode === m.key && !techFilter && !skillFilter)} onClick={() => selectMode(m.key)}>
                    {m.label}
                </div>
            ))}
        </div>

        {isAuth && (
            <div style={{ marginBottom: 8 }}>
                <Link to="/home" style={sidebarLinkStyle(false)}>My Projects</Link>
            </div>
        )}

        <p style={sidebarHeadingStyle}>Technology</p>
        {TECHNOLOGIES.map((t) => (
            <div key={t} style={sidebarLinkStyle(techFilter === t)} onClick={() => selectTech(t)}>
                {t}
            </div>
        ))}

        <p style={sidebarHeadingStyle}>Skill</p>
        {SKILLS.map((s) => (
            <div key={s} style={sidebarLinkStyle(skillFilter === s)} onClick={() => selectSkill(s)}>
                {s}
            </div>
        ))}

        {isAuth && (
            <>
                <div style={{ borderTop: '1px solid var(--border, #eee)', margin: '16px 0' }} />
                <Link to={`/${user.username}`} style={{ ...sidebarLinkStyle(false), fontSize: '0.8rem' }}>
                    Profile
                </Link>
                <Link to="/settings" style={{ ...sidebarLinkStyle(false), fontSize: '0.8rem' }}>
                    Settings
                </Link>
            </>
        )}
    </>
);
